import {
  type FieldData,
  LocalTimeExt,
  VisitedSectionExt,
} from '@castoredc/castor_prose';
import { convertToApplicationError } from 'features/error-handling/errorUtils';
import { logger } from 'features/logger/logger';
import {
  addPerformanceStartMark,
  addPerformanceEndMark,
} from 'features/monitoring/performanceMonitor';
import { selectParticipationDocData } from 'features/participations/store/participationSelectors';
import { getTimezoneName } from 'shared/utils/dateUtils';
import {
  callProseRender,
  callProseStartRender,
} from '../services/proseService';
import { selectMatchedActivityTaskDocData } from './activityTaskSlice';
import { questionnaireAssetApiEndpoints } from './questionnaireAssetApiSlice';
import { questionnaireResumeSubmissionThunk } from './questionnaireResumeSubmissionThunk';
import {
  selectCurrentSectionName,
  selectDocData,
  selectLoading,
  selectReadyToReceiveSnapshots,
  selectResumeInFlight,
} from './questionnaireSelectors';
import {
  applyProseOutput,
  enterPostQuestionnaire,
  errorOccurred,
  setTouchedFields,
} from './questionnaireSlice';
import type { AppPromiseThunk } from 'features/store/store';

export const questionnaireProseThunk =
  (fieldsData: FieldData, touchedFields: string[]): AppPromiseThunk =>
  async (dispatch, getState) => {
    try {
      logger.debug('questionnaireProseThunk invoked', {
        details: {
          fieldsData,
          touchedFields,
        },
      });

      addPerformanceStartMark('questionnaireProseThunk');

      const docData = selectDocData(getState());
      const participation = selectParticipationDocData(getState());
      const dslDefinition = participation.manifest.definition;

      const { data: dslSource } =
        questionnaireAssetApiEndpoints.getProseDslSourceByAssetId.select(
          dslDefinition
        )(getState());

      if (!dslSource) {
        throw new Error(
          `[Assert failed] Unable to select dslSource for ${dslDefinition} from questionnaireAssetApi slice`
        );
      }

      // PERF: If call to PROSE protocolService.render ever gets too expensive, consider skipping invoking it if document
      // update contains only changes to properties which are not passed to PROSE (e.g. touched fields).
      // Possible blocker for such optimization is the current time being passed as input to PROSE (validations
      // output might change).

      // start_render is only needed for a never-before-rendered questionnaire.
      // A null contextData means PROSE has never initialized this questionnaire.
      // After start_render, PROSE returns a full RenderContext that persists to
      // Firestore. On reload, a non-null contextData signals start_render was called.
      // Note: null check is inlined (not stored in a boolean) so TypeScript narrows
      // docData.contextData to non-null in the else branch.
      const currentSectionName = selectCurrentSectionName(getState());

      if (!docData.contextData) {
        const startOutput = await callProseStartRender({
          dslSource: dslSource,
          questionnaireName: docData.questionnaireName,
          timezone: getTimezoneName(),
        });
        // The render was awaited: the questionnaire may have been closed
        // (unmount reset the slice) in the meantime — apply nothing.
        if (!selectReadyToReceiveSnapshots(getState())) {
          return;
        }
        dispatch(applyProseOutput(startOutput));
      } else {
        const renderResult = await callProseRender({
          dslSource: dslSource,
          questionnaireName: docData.questionnaireName,
          fieldsData: fieldsData,
          contextData: {
            ...docData.contextData,
            localTime: LocalTimeExt.fromDate(new Date(), getTimezoneName()),
            ...(currentSectionName !== undefined && {
              currentSection: VisitedSectionExt.section(currentSectionName),
            }),
          },
        });

        // The render was awaited: the questionnaire may have been closed
        // (unmount reset the slice) in the meantime — apply nothing.
        if (!selectReadyToReceiveSnapshots(getState())) {
          return;
        }

        // callProseRender returns 'submitted' when the persisted RenderContext
        // still encodes the submission transition — an interrupted finish left
        // the end-context on the doc. Apply nothing: the doc's submission
        // context must stay intact for the server render. Resume the submission,
        // unless the outcome is already committed (live task doc completed) or a
        // resume already runs. The guards and dispatch must stay synchronous so
        // a concurrent render (e.g. a snapshot echo) cannot double-fire the
        // resume.
        if (renderResult.type === 'submitted') {
          const state = getState();
          const outcomeCommitted =
            selectMatchedActivityTaskDocData(
              state,
              participation.id,
              docData.activityName
            )?.status === 'completed';
          if (
            outcomeCommitted ||
            selectResumeInFlight(state) ||
            selectLoading(state)
          ) {
            return;
          }

          logger.warn('Interrupted submission detected — resuming', {
            details: {
              questionnaireId: docData.id,
              activityName: docData.activityName,
            },
          });
          void dispatch(questionnaireResumeSubmissionThunk(docData));
          return;
        }

        // callProseRender can return 'postQuestionnaire' when the persisted
        // RenderContext already encodes the post-questionnaire phase (set during
        // the original submission render and persisted to Firestore). This
        // happens on re-open of a task with status 'post-questionnaire'.
        if (renderResult.type === 'postQuestionnaire') {
          dispatch(enterPostQuestionnaire(renderResult.output));
        } else {
          dispatch(applyProseOutput(renderResult.output));
        }
      }

      dispatch(setTouchedFields(touchedFields));
    } catch (error) {
      const errorToLog = convertToApplicationError(error);
      logger.error('Error in questionnaireProseThunk', undefined, errorToLog);
      dispatch(errorOccurred(errorToLog.toErrorData()));
    } finally {
      addPerformanceEndMark('questionnaireProseThunk', {
        console: true,
      });
    }
  };
